import { normalizeSearchText } from "./catalog-search";
import { MACROS, canonicalCategory, macroOf, type MacroKey } from "./catalog-taxonomy";
import type { ListedPiece } from "./catalog-types";

/**
 * The catalog's taxonomy cascade (handoff §7.2, filters spec §4): department, then category.
 *
 * Two narrowing levels, each counted over the previous level's slice, and **every choice is
 * validated against what exists**: the selection goes in as a request and comes out resolved
 * rather than being corrected by an effect. `colecoes` is the prototype's exception, using the
 * piece's own collection as its second level. The type of piece, a third track until v1, is
 * an attribute since v2 (`catalog-facets.ts`, "Tipo").
 */

export const ALL_PIECES_LABEL = "Todas as peças";
export const ALL_CATEGORIES_LABEL = "Todas";
export const ALL_TYPES_LABEL = "Todos";

/**
 * What the second line is called. It is **the department that names it**: under "Coleções" the
 * line lists collections, and a line of collections called "Categorias" would be wrong.
 *
 * Both builders set it, and `TaxonomyRail` only reads it, so the five screens that draw the
 * catalog (the panel, the public list, a suggestion list and its preview, the creation's curation)
 * cannot disagree: there is one name, chosen in one place.
 */
export const CATEGORIES_LINE_LABEL = "Categorias";
export const COLLECTIONS_LINE_LABEL = "Coleções";

export type TaxonomySelection = {
  macro: MacroKey | null;
  /** In hand, the canonical category's name; over the served catalogue, the category's id. */
  category: string | null;
  /**
   * The third level, N3 (handoff §2 item 6): the type of piece, a single choice, drawn only over
   * the served catalogue and only once a category is chosen. A navigation, not a mark — the
   * attribute facet "Tipo" is the multi-choice sibling and the two never coexist (see
   * `facetTypesInScope`).
   */
  type: string | null;
};

export const EMPTY_TAXONOMY: TaxonomySelection = { macro: null, category: null, type: null };

export type RailOption = {
  /** Option key; the empty string is the level's "all" option. */
  value: string;
  label: string;
  /** Count; empty when the level does not count. */
  count: string;
  isActive: boolean;
};

export type CatalogRails = {
  /** The selection after validation against what exists. */
  selection: TaxonomySelection;
  macros: RailOption[];
  /** The second line: categories, or the collections under the "Coleções" department. */
  categories: RailOption[];
  /** What that line is called (`CATEGORIES_LINE_LABEL`, or `COLLECTIONS_LINE_LABEL`). */
  categoryLabel: string;
  /** N3: the types inside the chosen category. Empty where there is no third level. */
  types: RailOption[];
  /** Whether each level has anything to navigate beyond its "all" (filters spec §4). */
  hasMacroRail: boolean;
  hasCategoryRail: boolean;
  /** Handoff §2 item 6: the line exists only with two or more types in the scope. */
  hasTypeRail: boolean;
  /** The pieces left after the two levels. Base for the facets. */
  scoped: ListedPiece[];
  /**
   * The chosen macro's own slice, before category and type. The featured toggle counts over it,
   * as the prototype does (`BASE`), so the chip follows the macro and not what is open under it.
   */
  base: ListedPiece[];
};

function countBy(pieces: readonly ListedPiece[], predicate: (piece: ListedPiece) => boolean) {
  return pieces.filter(predicate).length;
}

/**
 * The prototype lists categories as its macro tables declare them (`cats(macro)`), not
 * alphabetically: Aparelho de Jantar, Prato, Prato de Pão, Sousplat, Bowl, … A category the
 * taxonomy does not know keeps to the end, in alphabetical order among its kind.
 *
 * With `MACROS` reduced to `colecoes` (see `catalog-taxonomy.ts`) the table lists no category, so
 * every one of them is "unknown" and the order is alphabetical. The ranking stays so the order
 * returns on its own the day the real table does.
 */
const TAXONOMY_ORDER: readonly string[] = MACROS.flatMap((macro) => macro.categories);

function byTaxonomyOrder(a: string, b: string): number {
  const rank = (category: string) => {
    const index = TAXONOMY_ORDER.indexOf(category);
    return index === -1 ? TAXONOMY_ORDER.length : index;
  };

  return rank(a) - rank(b) || a.localeCompare(b, "pt-BR");
}

/**
 * The category a piece sits under in the second level, **the one place that says so**: the chip's
 * value and the grid's slice both read it, so a chip can never offer a value the filter does not
 * find.
 *
 * The canonical name when the taxonomy table knows the category, otherwise the ERP's own name,
 * otherwise `null` (the piece has none). The table exists to group categories into departments
 * (`macroOf`) and says nothing about which categories may appear: gating the rail on it hid
 * about three quarters of a real list's categories (measured 2026-10-20 on the house templates).
 * `macroOf` still reads `canonicalCategory` alone — a category the table does not know has no
 * department, and that is the accepted state while the map to departments does not exist.
 */
export function railCategory(piece: Pick<ListedPiece, "category">): string | null {
  const canonical = canonicalCategory(piece);
  if (canonical !== null) return canonical;

  const own = piece.category.trim();

  return own === "" ? null : own;
}

/**
 * Every collection, most numerous first, breaking ties by name.
 *
 * **Never a cut.** This used to keep the twelve most numerous (the prototype's number), which is
 * harmless with a handful of collections and wrong with ninety-seven: under the "Coleções"
 * department the collection **is** the second level, and the attribute chip that could reach the
 * rest is hidden there, so a collection outside the first twelve could not be chosen at all. The
 * rail scrolls sideways, as the categories' does with its ninety-five.
 */
function collectionsByCount(pieces: readonly ListedPiece[]): string[] {
  const tally = new Map<string, number>();

  for (const piece of pieces) {
    if (piece.collection.length === 0) continue;
    tally.set(piece.collection, (tally.get(piece.collection) ?? 0) + 1);
  }

  return [...tally.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "pt-BR"))
    .map(([collection]) => collection);
}

export function buildCatalogRails(
  pieces: readonly ListedPiece[],
  /** Department and category only: in hand there is no third level (`selection.type` is null). */
  requested: Pick<TaxonomySelection, "macro" | "category">,
): CatalogRails {
  // Level 1: a macro only counts if it exists and holds pieces. `colecoes` is offered from
  // two collections up, since with one it would separate nothing.
  const collectionCount = new Set(
    pieces.map((piece) => piece.collection).filter((value) => value.length > 0),
  ).size;

  const availableMacros = MACROS.filter((macro) =>
    macro.key === "colecoes"
      ? collectionCount > 1
      : pieces.some((piece) => macroOf(piece) === macro.key),
  );

  const macro =
    requested.macro !== null && availableMacros.some((entry) => entry.key === requested.macro)
      ? requested.macro
      : null;

  const byCollection = macro === "colecoes";
  const base =
    macro !== null && !byCollection ? pieces.filter((piece) => macroOf(piece) === macro) : pieces;

  // Level 2: collections under `colecoes`, the macro's categories otherwise.
  const categoryValues = byCollection
    ? collectionsByCount(base)
    : Array.from(
        new Set(
          base
            .map((piece) => railCategory(piece))
            .filter((value): value is string => value !== null),
        ),
      ).sort(byTaxonomyOrder);

  const category =
    requested.category !== null && categoryValues.includes(requested.category)
      ? requested.category
      : null;

  const scoped =
    category === null
      ? [...base]
      : base.filter((piece) =>
          byCollection ? piece.collection === category : railCategory(piece) === category,
        );

  return {
    // In hand there is no type table: the pieces carry none, and "Tipo" stays an attribute.
    selection: { macro, category, type: null },
    types: [],
    hasTypeRail: false,
    // A track with nothing to offer but "all" separates nothing, so it does not draw.
    hasMacroRail: availableMacros.length > 0,
    hasCategoryRail: categoryValues.length > 0,
    categoryLabel: byCollection ? COLLECTIONS_LINE_LABEL : CATEGORIES_LINE_LABEL,
    macros: [
      {
        value: "",
        label: ALL_PIECES_LABEL,
        count: String(pieces.length),
        isActive: macro === null,
      },
      ...availableMacros.map((entry) => ({
        value: entry.key,
        label: entry.label,
        // `colecoes` counts **its collections**, not pieces: the total of pieces would repeat the
        // "all pieces" number and inform nobody, and how many collections there are to choose
        // from is what the chip can tell. It is the number of the line it opens.
        count:
          entry.key === "colecoes"
            ? String(collectionCount)
            : String(countBy(pieces, (piece) => macroOf(piece) === entry.key)),
        isActive: macro === entry.key,
      })),
    ],
    categories: [
      {
        value: "",
        label: ALL_CATEGORIES_LABEL,
        // v2 counts "Todas" too: the department's whole slice (`abaVals`, `BASE.length`).
        count: String(base.length),
        isActive: category === null,
      },
      ...categoryValues.map((value) => ({
        value,
        // Presentation is the rail's business and the value stays untouched. The ERP's capitals
        // ("AÇUCAREIRO") are set the way the served rail sets them; a collection's name is not a
        // category and is left as it is.
        label: byCollection ? value : displayCategoryName(value),
        count: String(
          countBy(base, (piece) =>
            byCollection ? piece.collection === value : railCategory(piece) === value,
          ),
        ),
        isActive: category === value,
      })),
    ],
    scoped,
    base: [...base],
  };
}

/** Words that stay lower case in a Brazilian title: "Bandeja de Lavabo", not "Bandeja De Lavabo". */
const LOWER_CASE_WORDS = new Set(["de", "da", "do", "das", "dos", "e", "para", "com", "em"]);

/**
 * The ERP writes categories in capitals ("XÍCARA DE CAFÉ E PIRES"). The value that travels in the
 * request is left exactly as the backend sent it; only what the person **reads** is set in title
 * case. The first word is always capitalised, connectors never.
 */
export function displayCategoryName(name: string): string {
  return name
    .toLocaleLowerCase("pt-BR")
    .split(" ")
    .map((word, index) =>
      index > 0 && LOWER_CASE_WORDS.has(word) ? word : word.charAt(0).toLocaleUpperCase("pt-BR") + word.slice(1),
    )
    .join(" ");
}

/**
 * The category line over the catalogue the server serves (handoff §4, "Categorias").
 *
 * The pieces of a served page carry no category of their own, so the line is built from the
 * `categories` aggregate and not from the pieces: `value` is the category's id, the same thing
 * `categoryId` sends. Most numerous first, ties by name — a rail of 95 reads from what people
 * actually find, and the order is stable between visits.
 *
 * **Two departments, as agreed for the MVP**: "Todas as peças" and "Coleções". The eight
 * merchandising departments are not drawn: the backend does not have them (`groups` holds a single
 * entry, TABLEWARE) and nobody has authored the map from the ERP's categories to them (not invented,
 * Gate_Externo; see `catalog-taxonomy.ts`). "Coleções" needs no map: the collections come with
 * their counts in the same aggregate, and under it the second line **is** the collections — the
 * line is renamed (`categoryLabel`) and a choice on it travels as `collectionId`, not `categoryId`.
 *
 * `selected` is validated against what exists, as `buildCatalogRails` does, and "Todas" counts
 * the whole of what the categories add up to.
 */
export function buildServedCategoryRails(
  categories: readonly { value: string; label: string; count: number }[],
  requested: Pick<TaxonomySelection, "category" | "type"> & { macro?: MacroKey | null },
  /** The types the server counted **inside the chosen category**; ignored without one. */
  typesInScope: readonly { value: string; label: string; count: number }[] = [],
  /** The collections the server counted, **the last complete list it gave us**. */
  collections: readonly { value: string; label: string; count: number }[] = [],
): CatalogRails {
  const byCount = (
    a: { label: string; count: number },
    b: { label: string; count: number },
  ) => b.count - a.count || a.label.localeCompare(b.label, "pt-BR");

  // As in hand, "Coleções" is offered from two collections up: with one it would separate nothing.
  const hasCollectionsDepartment = collections.length > 1;
  const byCollection = requested.macro === "colecoes" && hasCollectionsDepartment;

  const orderedCategories = [...categories].sort(byCount);
  const orderedCollections = [...collections].sort(byCount);
  // The second line's own options: one level, whatever it lists.
  const ordered = byCollection ? orderedCollections : orderedCategories;

  const chosen = ordered.find((entry) => entry.value === requested.category);
  const category = chosen?.value ?? null;
  // Every piece is in a category, not in a collection: the whole is what the categories add up
  // to, under either department (the same number as "Todas as peças").
  const total = orderedCategories.reduce((sum, entry) => sum + entry.count, 0);

  // N3 (handoff §2 item 6): only inside a category, and only with two or more types in it. A
  // collection has no types line of its own: "Tipo" stays the attribute it is.
  const typeValues = category === null || byCollection ? [] : typesInScope;
  const hasTypeRail = typeValues.length > 1;
  const type =
    hasTypeRail && requested.type !== null && typeValues.some((entry) => entry.value === requested.type)
      ? requested.type
      : null;

  return {
    selection: { macro: byCollection ? "colecoes" : null, category, type },
    // The department line draws only when there is a second department to choose.
    hasMacroRail: hasCollectionsDepartment,
    macros: hasCollectionsDepartment
      ? [
          { value: "", label: ALL_PIECES_LABEL, count: String(total), isActive: !byCollection },
          // The number of collections, as in hand: the total of pieces would repeat "Todas as peças".
          {
            value: "colecoes",
            label: COLLECTIONS_LINE_LABEL,
            count: String(collections.length),
            isActive: byCollection,
          },
        ]
      : [],
    hasTypeRail,
    types: hasTypeRail
      ? [
          // "Todos" counts the category's own slice, which the aggregate already holds.
          { value: "", label: ALL_TYPES_LABEL, count: String(chosen?.count ?? ""), isActive: type === null },
          ...typeValues.map((entry) => ({
            value: entry.value,
            label: entry.label,
            count: String(entry.count),
            isActive: type === entry.value,
          })),
        ]
      : [],
    hasCategoryRail: ordered.length > 0,
    categoryLabel: byCollection ? COLLECTIONS_LINE_LABEL : CATEGORIES_LINE_LABEL,
    categories: [
      { value: "", label: ALL_CATEGORIES_LABEL, count: String(total), isActive: category === null },
      ...ordered.map((entry) => ({
        value: entry.value,
        // A collection's name is not an ERP category in capitals: left as it is, as in hand.
        label: byCollection ? entry.label : displayCategoryName(entry.label),
        count: String(entry.count),
        isActive: category === entry.value,
      })),
    ],
    // The grid draws the server's page; there are no pieces in hand to scope.
    scoped: [],
    base: [],
  };
}

/**
 * Choosing a level **clears the ones below it**.
 *
 * `buildCatalogRails` would already guard the read, but keeping a stale choice in state makes
 * the rail flicker back if the person returns to the previous macro.
 */
export function selectMacro(macro: MacroKey | null): TaxonomySelection {
  return { macro, category: null, type: null };
}

export function selectCategory(
  selection: TaxonomySelection,
  category: string | null,
): TaxonomySelection {
  // Handoff §2: "Trocar categoria" zeroes the type.
  return { ...selection, category, type: null };
}

/** "Trocar tipo" zeroes the marks and the open facet, and **not** the department or category. */
export function selectType(selection: TaxonomySelection, type: string | null): TaxonomySelection {
  return { ...selection, type };
}

export function hasTaxonomySelection(selection: TaxonomySelection): boolean {
  return selection.macro !== null || selection.category !== null || selection.type !== null;
}

/**
 * The "Tipo" facet's options over the served catalogue, once the navigation has had its say
 * (handoff §5).
 *
 * - **A category never lends its own name to the facet.** "Bandeja" the category and "Bandeja"
 *   the type are one fact; offered again as a mark they would make the facet a clone of the
 *   line above it.
 * - **A type chosen in the N3 line is the whole scope**, so the facet holds that one value and,
 *   with fewer than two, does not draw. This is also what keeps the two controls from ever
 *   combining on the wire: the backend ORs repeated `attribute` values of the same slug
 *   (measured 2026-10-14: Vaso 48 + Bandeja 10 = 58), so a trail choice plus a facet mark would
 *   widen the grid instead of narrowing it.
 */
export function facetTypesInScope<T extends { value: string; label: string }>(
  types: readonly T[],
  categoryName: string | null,
  trailType: string | null,
): T[] {
  if (trailType !== null) return types.filter((entry) => entry.value === trailType);
  if (categoryName === null) return [...types];

  const category = normalizeSearchText(categoryName);

  return types.filter((entry) => normalizeSearchText(entry.label) !== category);
}
